import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import {
  CreditLimitLevel,
  CreditRepository,
  CustomerCreditBalance,
  customerCreditLevel,
} from '@softapps/core/customer-info';
import { Step } from '@softline/application';
import { askFn, NotificationComponent, ResolvableModal } from '@softline/ui-core';
import { ProgressStepsComponent } from '@softline/ui-core/progress';
import { SalesConfigService } from '../../services/sales-config.service';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../types/document-form-data';
import {
  isCustomerAddressComplete,
  isLastWizardStep,
  WizardStepKey,
  wizardStepAt,
  wizardStepKeys,
} from './document-creation-wizard.functions';
import { AddressStepComponent } from './steps/address-step/address-step.component';
import { ConstructionSiteStepComponent } from './steps/construction-site-step/construction-site-step.component';
import { CustomerStepComponent } from './steps/customer-step/customer-step.component';
import { DeliveryTypeStepComponent } from './steps/delivery-type-step/delivery-type-step.component';

@Component({
  selector: 'soft-document-creation-wizard',
  templateUrl: './document-creation-wizard.component.html',
  styleUrl: './document-creation-wizard.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col' },
  imports: [
    CurrencyPipe,
    ProgressStepsComponent,
    NotificationComponent,
    CustomerStepComponent,
    AddressStepComponent,
    ConstructionSiteStepComponent,
    DeliveryTypeStepComponent,
  ],
  providers: [CurrencyPipe],
})
export class DocumentCreationWizardComponent extends ResolvableModal<DocumentFormData> {
  private readonly deviceConfig = inject(SalesConfigService).config();
  private readonly creditRepository = inject(CreditRepository);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly ask = askFn();

  readonly initialCustomer = input<DocumentFormData['customer'] | null>(null);
  /** Pre-selected construction site id (e.g. clicked from the customer side
   *  panel). When set, the Baustelle step is pre-filled and the wizard opens on
   *  the Lieferart step. */
  readonly initialConstructionSiteId = input<number | null>(null);
  /** Display name of the pre-selected construction site for the stepper. */
  readonly initialConstructionSiteName = input<string | null>(null);

  readonly navTabIndex = signal(0);

  readonly documentData = signal<DocumentFormData>({
    ...EMPTY_DOCUMENT_FORM_DATA,
    distributionTypeId: this.deviceConfig.defaultDistributionType
      ? Number(this.deviceConfig.defaultDistributionType)
      : null,
  });

  private readonly _seedFromInitialCustomer = effect(() => {
    const customer = this.initialCustomer();
    if (!customer) return;
    const constructionSiteId = untracked(() => this.initialConstructionSiteId());
    const constructionSiteName = untracked(() => this.initialConstructionSiteName());
    untracked(() => {
      this.documentData.update((d) => ({
        ...d,
        customer,
        constructionSiteId,
        constructionSiteName,
      }));
      // With a pre-selected site, skip ahead to Lieferart; otherwise land on
      // the step after Kunde — Baustelle, or Lieferart for a Barverkauf customer.
      const deliveryTypeIndex = this.stepKeys().indexOf('deliveryType');
      this.navTabIndex.set(constructionSiteId != null ? deliveryTypeIndex : 1);
    });
  });

  /** Switching to a regular customer drops what the Adresse step collected —
   *  nothing else in the wizard turns the manual Kundenadresse on, so it would
   *  otherwise ship on a customer that has a master-data address. */
  private readonly _clearAddressForRegularCustomer = effect(() => {
    if (this.documentData().customer?.oneTimeCustomer) return;
    untracked(() => {
      if (!this.documentData().manualCustomerAddressEnabled) return;
      this.documentData.update((d) => ({
        ...d,
        manualCustomerAddressEnabled: false,
        manualAddressValue: null,
      }));
    });
  });

  /** A Barverkauf account pools every walk-in customer, so its balance says
   *  nothing about this buyer — the check is skipped, as in the customer panel. */
  private readonly creditCustomerId = computed<string | null>(() => {
    const customer = this.documentData().customer;
    return customer && !customer.oneTimeCustomer && customer.id
      ? String(customer.id)
      : null;
  });

  private readonly creditBalance = signal<CustomerCreditBalance | null>(null);
  /** The request in flight — the submit gate awaits it, so picking a customer
   *  and creating the document straight away is still checked. */
  private creditRequest: Promise<void> = Promise.resolve();

  private readonly _loadCreditBalance = effect(() => {
    const id = this.creditCustomerId();
    untracked(() => {
      this.creditBalance.set(null);
      if (id) this.creditRequest = this.loadCreditBalance(id);
    });
  });

  private readonly creditLevel = computed<CreditLimitLevel | null>(() =>
    customerCreditLevel(this.creditBalance())
  );

  readonly creditNotice = computed(() => {
    const balance = this.creditBalance();
    if (!balance) return null;
    switch (this.creditLevel()) {
      case 'limit':
        return {
          status: 'danger' as const,
          title: 'Kreditlimit überschritten',
          thresholdLabel: 'Kreditlimit',
          threshold: balance.limit,
          balance: balance.balance,
        };
      case 'warning':
        return {
          status: 'warning' as const,
          title: 'Kreditlimit-Warnschwelle überschritten',
          thresholdLabel: 'Warnschwelle',
          threshold: balance.warningLimit,
          balance: balance.balance,
        };
      default:
        return null;
    }
  });

  /** Advisory here — an unreachable credit endpoint must not stop the document
   *  from being created; the finalize check is the blocking one. */
  private async loadCreditBalance(id: string): Promise<void> {
    const balance = await this.creditRepository
      .getCustomerCreditBalance(id)
      .catch(() => null);
    if (this.creditCustomerId() === id) this.creditBalance.set(balance);
  }

  readonly isPickup = signal(false);

  readonly stepKeys = computed<WizardStepKey[]>(() =>
    wizardStepKeys(this.documentData().customer)
  );

  readonly activeStep = computed<WizardStepKey>(() =>
    wizardStepAt(this.stepKeys(), this.navTabIndex())
  );

  onDeliveryTypeChange(data: DocumentFormData): void {
    this.documentData.set(data);
    this.isPickup.set(data.deliveryType?.toLowerCase().includes('abholung') ?? false);
  }

  readonly steps = computed<Step[]>(() => {
    const d = this.documentData();
    return this.stepKeys().map((key, index) => {
      switch (key) {
        case 'customer':
          return {
            header: 'Kunde',
            subHeader: d.customer
              ? [
                  d.customer.customerNumber,
                  [d.customer.name1, d.customer.name2].filter(Boolean).join(' '),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Kunde wählen',
            done: !!d.customer,
            execute: () => this.navTabIndex.set(index),
          };
        case 'address':
          return {
            header: 'Adresse',
            subHeader: isCustomerAddressComplete(d.manualAddressValue)
              ? [d.manualAddressValue?.name1, d.manualAddressValue?.city]
                  .filter(Boolean)
                  .join(' · ')
              : 'Adresse erfassen',
            done: isCustomerAddressComplete(d.manualAddressValue),
            execute: () => this.navTabIndex.set(index),
          };
        case 'constructionSite':
          return {
            header: 'Baustelle',
            subHeader:
              d.constructionSite ??
              d.constructionSiteName ??
              (d.constructionSiteId != null ? 'Baustelle gewählt' : 'Baustelle wählen'),
            done: !!d.constructionSite || d.constructionSiteId != null,
            execute: () => this.navTabIndex.set(index),
          };
        case 'deliveryType':
          return {
            header: 'Lieferart',
            subHeader: d.deliveryTypeLabel ?? d.deliveryType ?? 'Lieferart wählen',
            done: !!d.deliveryType,
            execute: () => this.navTabIndex.set(index),
          };
      }
    });
  });

  onNext(data: DocumentFormData): void {
    this.documentData.set(data);
    if (isLastWizardStep(this.stepKeys(), this.navTabIndex())) {
      void this.submit();
    } else {
      this.navTabIndex.update((i) => i + 1);
    }
  }

  onCreate(data: DocumentFormData): void {
    this.documentData.set(data);
    void this.submit();
  }

  async submit(): Promise<void> {
    if (!(await this.passesCreditLimit())) return;
    this.resolve(this.documentData());
  }

  /** Over the hard limit the document is still creatable — an Offert or
   *  Kassabon has to stay possible — so this asks instead of blocking. The
   *  warning threshold is carried by the banner alone. */
  private async passesCreditLimit(): Promise<boolean> {
    await this.creditRequest;
    const notice = this.creditNotice();
    if (notice?.status !== 'danger') return true;
    const amount = (value: number): string =>
      this.currencyPipe.transform(value, 'EUR', 'symbol', '1.2-2', 'de-AT') ?? '';
    const answer = await this.ask({
      title: notice.title,
      subtitle: `Kreditlimit ${amount(notice.threshold)}`,
      question:
        `Offener Saldo: ${amount(notice.balance)}\n\n` +
        'Beleg trotzdem anlegen? Der Abschluss ist erst nach Klärung des ' +
        'Kreditlimits möglich.',
    });
    return answer === 'YES';
  }
}
